import type {
  ConditionConfiguration,
  FieldConfiguration,
} from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { CalculateByField, FieldConfigurationType } from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  AreaItemType,
  CustomColumnDescriptor,
  CustomColumnField,
  MeasureDescriptor,
  MeasureField,
  MetricDescriptor,
  MetricField,
  ReportField,
} from "../../../../../../../api/types/svTypes.ts";
import { DimensionFieldType, MeasureUnitTable } from "../../../../../../../api/types/svTypes.ts";
import { checkUnitTable } from "../../../utilities/checkUnitTable.ts";
import { getConditionLeafFilters } from "../../../utilities/conditionDescriptorHelper.ts";
import { isMeasure } from "../../../utilities/fieldsHelper.ts";
import { toAmountType, toCalculateBy } from "../../../utilities/measureConverter.ts";
import { createFieldId, keepOnlyLettersAndNumbers } from "../common/utilities/createFieldId.ts";

export class MeasureUtils {
  static createCustomColumnField(field: CustomColumnDescriptor, areaItemType: AreaItemType): CustomColumnField {
    const valueField: CustomColumnField = {
      type: DimensionFieldType.CUSTOM,
      meta: field,
      areaItemType,
      config: {
        ...field.configuration,
        type: FieldConfigurationType.Custom,
        guid: field.configuration?.guid ?? createFieldId(keepOnlyLettersAndNumbers(field.name)),
        name: field.name,
      },
    };
    return valueField;
  }
  static createMeasureField(field: MeasureDescriptor, areaItemType: AreaItemType): MeasureField {
    const valueField: MeasureField = {
      type: DimensionFieldType.MEASURE,
      meta: field,
      areaItemType,
      config: {
        type: FieldConfigurationType.Measure,
        ...field.configuration,
        guid: createFieldId(field.name),
        name: field.name,
        customConditions: field.customConditions?.length
          ? getConditionLeafFilters(field.customConditions).map((filter): ConditionConfiguration => ({
              filter,
              guid: createFieldId(filter.dimensionName),
            }))
          : undefined,
        useCustomConditions: (field.customConditions?.length || 0) > 0,
        calculateByField: toCalculateBy(field.units),
        amountType: toAmountType(field.units),
      },
    };

    return valueField;
  }

  static createMetricField(field: MetricDescriptor, areaItemType: AreaItemType): MetricField {
    const valueField: MetricField = {
      type: DimensionFieldType.METRIC,
      meta: field,
      areaItemType,
      config: {
        type: FieldConfigurationType.Metric,
        ...field.configuration,
        guid: createFieldId(field.name),
        name: field.name,
        customConditions: field.customConditions?.length
          ? getConditionLeafFilters(field.customConditions).map((filter): ConditionConfiguration => ({
              filter,
              guid: createFieldId(filter.dimensionName),
            }))
          : undefined,
      },
    };

    return valueField;
  }
}

export function updateCalculateByFieldForCustomMeasures<
  T extends {
    updateMeasure: (field: ReportField, changes: Partial<ReportField>) => void;
    updateMeasureConfig: (field: ReportField, changes: Partial<FieldConfiguration>) => void;
    removeMeasure: (field: ReportField) => void;
    values: ReportField[];
  },
>(fieldsState: T, measures: MeasureDescriptor[]) {
  if (fieldsState.values.length === 0) return;
  fieldsState.values.forEach((field) => {
    if (!isMeasure(field)) {
      return;
    }
    const measure = measures.find((m) => m.name === field.meta.name && m.custom);
    if (measure) {
      const calculatedBy = toCalculateBy(measure.units);
      if (field.config.calculateByField === undefined) {
        const unitsChanged = calculatedBy !== field.config.calculateByField;
        if (unitsChanged) {
          fieldsState.updateMeasureConfig(field, {
            calculateByField: calculatedBy,
          });
        }
      }
      fieldsState.updateMeasure(field, { meta: measure });
    } else {
      const measureWasRemoved = !measures.find((m) => m.name === field.meta.name);
      if (measureWasRemoved) {
        fieldsState.removeMeasure(field);
      }
    }
  });
}

export function updateMeasureFields<
  T extends {
    updateMeasureField: (field: ReportField, changes: Partial<ReportField>) => void;
    updateFieldConfig: (field: ReportField, changes: Partial<FieldConfiguration>) => void;
    removeField: (field: ReportField) => void;
    fields: ReportField[];
  },
>(fieldsState: T, measures: MeasureDescriptor[]) {
  if (fieldsState.fields.length === 0) return;
  fieldsState.fields.forEach((field) => {
    if (isMeasure(field)) {
      const measure = measures.find((m) => m.name === field.meta.name);
      if (measure !== undefined) {
        const calculatedBy = toCalculateBy(measure.units);
        if (field.config.calculateByField === undefined) {
          const unitsChanged = calculatedBy !== field.config.calculateByField;
          if (unitsChanged) {
            fieldsState.updateFieldConfig(field, { calculateByField: calculatedBy });
          }
        }
        fieldsState.updateMeasureField(field, { meta: measure });
      } else {
        const measureWasRemoved = !measures.find((m) => m.name === field.meta.name);
        if (measureWasRemoved) {
          fieldsState.removeField(field);
        }
      }
    }
  });
}

export function isGlTableMeasure(meta: Pick<MeasureDescriptor, "units">) {
  return !Object.values(meta.units).some((unit) => checkUnitTable(unit.table, MeasureUnitTable.Memo));
}

export function isSharesRelatedMeasure(measure: MeasureField) {
  return measure.config.calculateByField === CalculateByField.Shares;
}
